import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductCard } from "../components/ProductCard";
import { MolecularBg } from "../components/MolecularBg";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import { useSEO } from "../lib/useSEO";

const CATEGORIES = [
    { slug: "", name: "All" },
    { slug: "glp-research", name: "GLP" },
    { slug: "recovery", name: "Recovery" },
    { slug: "longevity", name: "Longevity" },
    { slug: "cognitive", name: "Cognitive" },
    { slug: "lab-supplies", name: "Lab Supplies" },
];

const SORTS = [
    { v: "", label: "Featured" },
    { v: "newest", label: "Newest" },
    { v: "rating", label: "Top Rated" },
    { v: "price-asc", label: "Price ↑" },
    { v: "price-desc", label: "Price ↓" },
];

export const ShopPage = () => {
    useSEO({
        title: "Shop Research Peptides — KLOW, Retatrutide, NAD+ & More · Carbon Labs AU",
        description: "Browse Carbon Labs' premium catalogue of 99%-purity research peptides. KLOW, Retatrutide, NAD+, Ipamorelin, MOTS-C and more. Manufacturer-supplied Certificate of Analysis available, same-day Sydney dispatch.",
    });
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCategory = searchParams.get("category") || "";
    const initialFilter = searchParams.get("filter") || "";

    const [category, setCategory] = useState(initialCategory);
    const [filter, setFilter] = useState(initialFilter);
    const [sort, setSort] = useState("");
    const [search, setSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const params = useMemo(() => {
        const p = {};
        if (category) p.category = category;
        if (filter === "new") p.is_new = true;
        if (filter === "bestseller") p.bestseller = true;
        if (sort) p.sort = sort;
        if (search) p.search = search;
        return p;
    }, [category, filter, sort, search]);

    useEffect(() => {
        setLoading(true);
        let cancelled = false;
        api.get("/products", { params })
            .then((r) => {
                if (cancelled) return;
                setProducts(r.data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setProducts([]);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [params]);

    useEffect(() => {
        setCategory(searchParams.get("category") || "");
        setFilter(searchParams.get("filter") || "");
    }, [searchParams]);

    const updateCategory = (c) => {
        setCategory(c);
        const sp = new URLSearchParams(searchParams);
        if (c) sp.set("category", c); else sp.delete("category");
        setSearchParams(sp);
    };

    const updateFilter = (f) => {
        setFilter(f);
        const sp = new URLSearchParams(searchParams);
        if (f) sp.set("filter", f); else sp.delete("filter");
        setSearchParams(sp);
    };

    return (
        <div className="pt-28 pb-16 relative min-h-screen" data-testid="shop-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <div className="mb-10">
                    <span className="eyebrow">// Catalog</span>
                    <h1 className="font-display text-4xl md:text-6xl font-extrabold text-white tracking-tighter mt-2">
                        Shop Peptides
                    </h1>
                    <p className="text-[var(--text-secondary)] mt-3 max-w-xl">
                        Browse the full catalogue of premium research peptides. Filter by category or research target.
                    </p>
                </div>

                {/* Filter bar */}
                <div className="glass-elevated rounded-2xl p-4 md:p-5 mb-8 flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search peptides..."
                            className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                            data-testid="shop-search-input"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => updateCategory(c.slug)}
                                data-testid={`filter-category-${c.slug || "all"}`}
                                className={`px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                                    category === c.slug
                                        ? "border-[var(--neon-cyan)] text-[var(--neon-cyan)] bg-[rgba(0,242,254,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <SlidersHorizontal size={14} className="text-[var(--text-muted)]" />
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="bg-black/40 border border-white/10 rounded-full px-3 py-2 text-xs text-white focus:outline-none focus:border-[var(--neon-cyan)]"
                            data-testid="shop-sort-select"
                        >
                            {SORTS.map((s) => (
                                <option key={s.v} value={s.v}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Active filter chips */}
                {filter && (
                    <div className="mb-6 flex items-center gap-2">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Active:</span>
                        <button
                            onClick={() => updateFilter("")}
                            className="px-3 py-1 rounded-full text-xs border border-[var(--neon-purple)] text-[var(--neon-purple)] bg-[rgba(177,77,255,0.08)]"
                            data-testid="active-filter-chip"
                        >
                            {filter === "new" ? "New Arrivals" : "Bestsellers"} ✕
                        </button>
                    </div>
                )}

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="aspect-[3/4] rounded-2xl glass animate-pulse" />
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-20 text-[var(--text-secondary)]" data-testid="shop-empty-state">
                        No products match your filters.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-6" data-testid="shop-product-grid">
                        {products.map((p) => (
                            <ProductCard key={p.slug} product={p} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
